// src/features/tasks/components/TaskCard.jsx
import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  LinearProgress,
  Tooltip,
  Stack,
  Button,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Utils
import {
  getStateConfig,
  getPriorityColor,
  isTaskOverdue,
} from '../utils/taskStateUtils';
import { calculateDeadlineProgress } from '../utils/deadlineProgressUtils';
import DateWithHint from '../../../components/common/DateWithHint';
import AssigneeCell from './TaskListPage/TasksDataGrid/components/AssigneeCell';
import TaskActionButtons from './TaskActionButtons';
import CategoryChip from '../../../components/common/CategoryChip';

export default function TaskCard({
  task,
  onEdit,
  onDelete,
  onView,
  onStateChange,
  canEdit = false,
  canDelete = false,
  canChangeState = false,
  hideContextMenu = false,
  showDescriptionOnHover = false,
}) {
  const theme = useTheme();
  const { t } = useTranslation(['tasks', 'common']);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMenuOpen = useCallback((event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleAction = useCallback((action) => {
    handleMenuClose();
    action(task);
  }, [task, handleMenuClose]);

  // Calculate overdue status using utility function
  const isOverdue = isTaskOverdue(task);

  // Get priority color using utility function
  const priorityColor = getPriorityColor(task.priority);

  // Get state config using utility function
  const stateConfig = getStateConfig(task.state);
  const StateIcon = stateConfig.icon;

  // Calculate deadline progress using utility function
  const { percent: progressPercent, barColor: progressBarColor } = calculateDeadlineProgress({
    scheduledDate: task.scheduled_date,
    dueDate: task.due_date,
    completionDate: task.completion_date,
  });

  // Determine if description should be visible
  const showDescription = showDescriptionOnHover ? isHovered : true;

  return (
    <>
      <Card 
        sx={{ 
          height: '100%', 
          position: 'relative',
          cursor: 'pointer',
          background: theme.palette.background.paper,
          '&:hover': { 
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease-in-out',
          ...(isOverdue && {
            borderLeft: '4px solid',
            borderLeftColor: 'error.main',
          }),
          ...(task.state === 'SCHEDULED' && {
            borderLeft: '4px solid',
            borderLeftColor: 'info.main',
          }),
        }}
        onClick={() => onView(task)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Progress indicator */}
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 3,
            backgroundColor: 'background.paper',
            '& .MuiLinearProgress-bar': {
              backgroundColor: progressBarColor,
            }
          }}
        />

        <CardContent sx={{ pb: 2 }}>
          {/* Header */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box flex={1} overflow="hidden">
              <Box 
                display="flex" 
                alignItems="center" 
                gap={1} 
                mb={2}
              >
                <AssignmentIcon fontSize="large" color="action" />
                <Typography variant="h4" fontWeight="bold" noWrap>
                  {task.title}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  icon={<StateIcon />}
                  label={stateConfig.label}
                  size="small"
                  color={stateConfig.color}
                  variant="outlined"
                />
                
                <Chip
                  label={t(`tasks:priority.${task.priority.toLowerCase()}`)}
                  size="small"
                  color={priorityColor}
                />

                {isOverdue && (
                  <Chip
                    icon={<WarningIcon />}
                    label={t('tasks:task_card.overdue')}
                    size="small"
                    color="error"
                    variant="filled"
                  />
                )}

                {task.state === 'SCHEDULED' && (
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={t('tasks:task_card.awaiting_start')}
                    size="small"
                    color="info"
                    variant="filled"
                  />
                )}
              </Box>
            </Box>
            
            {!hideContextMenu && (
              <IconButton size="small" onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>

          {/* Description - Animated fly-down */}
          {task.description && (
            <Collapse in={showDescription} timeout={300}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                backgroundColor="background.default"
                borderRadius={3}
                border="10px solid"
                borderColor="background.default"
                boxShadow={`0 0 0 1px ${theme.palette.divider}`}
                sx={{ 
                  mb: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {task.description}
              </Typography>
            </Collapse>
          )}

          {/* Assignees */}
          <Box display={"flex"} alignItems={"center"} my={3}>
            <AssigneeCell row={task} />

            <Box flex={1} />

            {/* Category */}
            {task.category?.name && <CategoryChip name={task.category.name} color={task.category.color_code} />}
          </Box>



          {/* Dates */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
            <DateWithHint
              date={task.scheduled_date}
              label={t('tasks:task_card.scheduled_label')}
              hintType="auto"
              showDate={false}
              highlightUpcoming={false}
              highlightOverdue={false}
            />
            
            <DateWithHint
              date={task.due_date}
              label={t('tasks:task_card.due_label')}
              hintType="countdown"
              showDate={false}
              highlightOverdue={true}
              highlightUpcoming={true}
              upcomingThreshold={7}
            />
          </Box>

          {/* Quick Actions - Dynamically generated using utility function */}
          {canChangeState && task.state !== 'SCHEDULED' && (
            <Box mt={4}>
              <TaskActionButtons
                task={task}
                onStateChange={onStateChange}
                variant="card"
                size="small"
              />
            </Box>
          )}

          {/* Message for SCHEDULED state */}
          {task.state === 'SCHEDULED' && (
            <Box mt={2}>
              <Typography 
                variant="body2" 
                color="info.main" 
                textAlign="center"
                sx={{ 
                  fontStyle: 'italic',
                  backgroundColor: 'info.50',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {t('tasks:task_card.scheduled_message')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 3 }}
      >
        <MenuItem onClick={() => handleAction(onView)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('tasks:task_card.view_details')}</ListItemText>
        </MenuItem>

        {canEdit && (
          <MenuItem onClick={() => handleAction(onEdit)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('tasks:task_card.edit_task')}</ListItemText>
          </MenuItem>
        )}

        {/* State change actions - Dynamically generated using utility function */}
        {canChangeState && task.state !== 'SCHEDULED' && (
          <TaskActionButtons
            task={task}
            onStateChange={(task, action) => handleAction(() => onStateChange(task, action))}
            variant="menuItems"
          />
        )}

        {canDelete && (
          <MenuItem onClick={() => handleAction(onDelete)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('tasks:task_card.delete_task')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}